import { yupResolver } from '@hookform/resolvers/yup';
import { useSignal } from '@preact/signals-react';
import clsx from 'clsx';
import { cloneDeep, isEqual } from 'lodash';
import type { FormEventHandler, Ref } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { ErrorOption, FieldErrors, FieldValues, ValidationMode } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { debounceTime, forkJoin, Observable, Subject, takeUntil } from 'rxjs';
import * as yup from 'yup';
import type { OdinDataRetrieval } from '../../common/data-retrieval/data-retrieval-interfaces';
import { OdinDataSender } from '../../common/data-retrieval/odin-data-sender';
import useTestTag from '../../common/hooks/use-test-tag';
import type { OdinKey } from '../../common/types/odin-key.type';
import Accordion from '../accordion/accordion.component';
import Button from '../button/button';
import { isEmptyObject } from '../common-functions';
import { DomElementAlignment, DomTargetPosition } from '../enums';
import type { JsonDataItem } from '../generic-types';
import TooltipLegacy from '../tooltip-component/tooltip.component';
import type { DataProxyService, ProxyValidationFunction } from './data-proxy.service';
import { DataProxyInstanceService } from './data-proxy-instance.service';
import DynamicField from './dynamic-field.component';
import { DynamicFieldService } from './dynamic-field.service';
import type {
  DynamicFormButtonSetting,
  DynamicFormError,
  DynamicFormField,
  DynamicFormParentFieldValue,
  DynamicFormSettings,
  FieldChangedFunction,
  PostSubmitHandler,
  PreSubmitHandler,
  RegisterPostSubmitFunction,
  RegisterPreSubmitFunction,
} from './dynamic-form.interfaces';
import { DynamicFormButtonLocation, DynamicFormButtonPosition } from './dynamic-form.interfaces';
import {
  buildParentFieldMap,
  getFieldSettingById,
  getFieldSettingByName,
  getPreSubmitData,
  getRequiredFields,
  isDataLoadedField,
  isFieldHidden,
} from './dynamic-form-common';
import type { VisibilityConditionConfig } from './visibility-conditions/visibility-condition.interfaces';
import { VisibilityConditionProcessor } from './visibility-conditions/visibility-condition-processor';

export interface DynamicFormRef {
  expandAllGroups: () => void;
  collapseAllGroups: () => void;
  submitForm: (disableValidation?: boolean) => void;
  moveGroupToTop: (groupId: string) => void;
  scrollFieldIntoView: (fieldId: string) => void;
  loadData: (data: JsonDataItem) => void;
  getData: () => JsonDataItem;
  formIsDirty: () => boolean;
  formDirtyFields: () => JsonDataItem;
  resetFormState: (currentState: JsonDataItem) => void;
  resetFormField: (name: string, value: unknown) => void;
  setFieldValue: (name: string, value: unknown) => void;
  setFieldError: (name: string, error: ErrorOption) => void;
  validate: () => Promise<boolean>;
  getFieldErrors: () => FieldErrors<FieldValues>;
}

export interface DynamicFormProps {
  dynamicFormId: string;
  settings: DynamicFormSettings;
  data?: JsonDataItem;
  onGetPreSubmitData?: () => void;
  onPreSubmit?: PreSubmitHandler;
  /**
   * Called when the form is submitted; this allows for the user to respond with the saved record id. This record id is
   * sent to the components that are listening for the post submit update.
   * @param {JsonDataItem} data - The forms data in JSON format.
   * @param {OdinDataSender<OdinKey | undefined>} sender - Allows the user to report the saved id back to the form.
   */
  onSubmit: (data: JsonDataItem, sender: OdinDataSender<OdinKey | undefined>) => void;
  onPostSubmit?: PostSubmitHandler;
  dataRetrieval?: OdinDataRetrieval;
  showSubmitButtons?: boolean;
  buttons?: Array<DynamicFormButtonSetting>;
  buttonStyles?: string;
  buttonsLocation?: DynamicFormButtonLocation;
  buttonsPosition?: DynamicFormButtonPosition;
  errors?: Record<string, DynamicFormError>;
  readOnly?: boolean;
  onFieldChanged?: FieldChangedFunction;
  /**
   * Fired when a dynamic field setting is changed.
   * @param {Array<DynamicFormField>} fields - The current state of the dynamic form fields.
   */
  onFormFieldsChanged?: (fields: Array<DynamicFormField>) => void;
  onAllGroupsExpanded?: () => void;
  onAllGroupsCollapsed?: () => void;
  /**
   * Hook Form specific props
   */
  onError?: () => void;
  validationEnabled?: boolean;
  formMode?: keyof ValidationMode;
  keyField?: keyof DynamicFormField;
}

const _fieldGroupId = (dynamicFormId: string, groupId: OdinKey) => `${dynamicFormId}_group_${groupId}`;
const _fieldId = (dynamicFormId: string, fieldId: OdinKey) => `${dynamicFormId}_field_${fieldId}`;

const DynamicForm = (props: DynamicFormProps, ref: Ref<DynamicFormRef>) => {
  const {
    dynamicFormId,
    settings,
    data,
    onGetPreSubmitData,
    onPreSubmit,
    onSubmit,
    onPostSubmit,
    dataRetrieval,
    showSubmitButtons = true,
    buttons,
    buttonStyles,
    buttonsLocation = DynamicFormButtonLocation.Left,
    buttonsPosition = DynamicFormButtonPosition.Inline,
    errors,
    readOnly,
    onFieldChanged,
    onFormFieldsChanged,
    onAllGroupsExpanded,
    onAllGroupsCollapsed,
    onError,
    validationEnabled = true,
    formMode = 'onSubmit',
    keyField = 'fieldName',
  } = props;

  const isAllowedToSubmit = useRef(false);
  const submitButtonRef = useRef<HTMLButtonElement | null>(null);
  const dataProxyService = useRef<DataProxyService | undefined>(undefined);
  const dataSender = useRef<OdinDataSender<OdinKey | undefined> | undefined>(undefined);
  const fieldsRef = useRef<Array<DynamicFormField> | undefined>(undefined);
  const visibilityConditionProcessor = useRef<VisibilityConditionProcessor | undefined>(undefined);
  const destroySubject = useRef<Subject<void>>(new Subject<void>());
  const beforeSubmitObservables = useRef<Array<Observable<JsonDataItem>>>([]);
  const afterSubmitObservables = useRef<Array<(id: OdinKey) => (sender: OdinDataSender<void>) => void>>([]);
  const parentFieldMap = useRef<Map<string, Array<string>> | undefined>(undefined);
  const workflowStepVisibilityConditionsRef = useRef<Array<VisibilityConditionConfig> | undefined>(undefined);
  const formButtonsRef = useRef<Array<DynamicFormButtonSetting> | undefined>(undefined);
  const isPostSubmitRef = useRef(false);

  const [dataProxy, setDataProxy] = useState<JsonDataItem>();
  const [fields, setFields] = useState<Array<DynamicFormField>>();
  const [formButtons, setFormButtons] = useState<Array<DynamicFormButtonSetting>>();
  const [isValidationEnabled, setIsValidationEnabled] = useState(validationEnabled);

  formButtonsRef.current = formButtons;
  const requiredFields = useMemo(() => getRequiredFields(fields ?? [], keyField), [fields, keyField]);
  const schema = useMemo(() => yup.object().shape(requiredFields), [requiredFields]);

  const tagCreator = useTestTag('dynamic-form');
  const parentValue = useSignal<Array<DynamicFormParentFieldValue>>([]);

  const {
    control,
    register,
    trigger,
    handleSubmit,
    setValue,
    getValues,
    reset,
    resetField,
    setError,
    formState: { dirtyFields, errors: hfErrors },
  } = useForm({
    resolver: isValidationEnabled
      ? yupResolver(schema)
      : (values) => {
          return { values, errors: {} };
        },
    mode: formMode,
  });

  const registerPreSubmitHandler = useCallback<RegisterPreSubmitFunction>(
    (callback: (sender: OdinDataSender<JsonDataItem>) => void) => {
      beforeSubmitObservables.current.push(
        new Observable<JsonDataItem>((subscriber) => callback(new OdinDataSender<JsonDataItem>(subscriber)))
      );
    },
    []
  );

  const registerPostSubmitHandler = useCallback<RegisterPostSubmitFunction>(
    (callback: (id: OdinKey) => (sender: OdinDataSender<void>) => void) => {
      afterSubmitObservables.current.push(callback);
    },
    []
  );

  const registerMethods = useMemo<{
    registerPreSubmitHandler: RegisterPreSubmitFunction;
    registerPostSubmitHandler: RegisterPostSubmitFunction;
  }>(() => {
    return {
      registerPreSubmitHandler,
      registerPostSubmitHandler,
    };
  }, [registerPreSubmitHandler, registerPostSubmitHandler]);

  useEffect(() => {
    if (buttons) {
      setFormButtons(buttons);
    }
  }, [buttons]);

  /**
   * The default function will notify the user of the field change and allow it to happen.
   * @type {(property, oldValue, newValue) => boolean}
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional: onFieldChanged and parentValue are used but shouldn't trigger recreating this stable callback
  const dataChangeValidation: ProxyValidationFunction = useCallback<ProxyValidationFunction>(
    (property, _oldValue, newValue) => {
      // User-driven field change clears the post-submit guard so that
      // subsequent visibility transitions correctly mark fields as dirty.
      isPostSubmitRef.current = false;

      if (fieldsRef.current) {
        const fieldSetting = getFieldSettingByName(property, fieldsRef.current);
        if (fieldSetting) {
          onFieldChanged?.(fieldSetting.id, property, fieldSetting.fieldType, newValue, fieldSetting.customFieldType);
          if (parentFieldMap.current?.has(fieldSetting.fieldName)) {
            const parentField = parentValue.value.find((item) => item.parentFieldId === fieldSetting.fieldName);
            const updatedParentField = {
              parentFieldId: fieldSetting.fieldName,
              fieldValue: newValue,
              type: fieldSetting.fieldType,
              initialValue: false,
            };
            if (parentField) {
              const index = parentValue.value.indexOf(parentField);
              //toSplited might not be supported by default, thus we need to create
              //own version of it
              const newParentValues = cloneDeep(parentValue.value);
              newParentValues.splice(index, 1, updatedParentField);
              parentValue.value = newParentValues;
            } else {
              parentValue.value = [...parentValue.value, updatedParentField];
            }
          }
        }

        visibilityConditionProcessor.current?.processChangedField(property, newValue);
      }
    },
    []
  );

  const reconcileButtonsByVisibility = (next: DynamicFormButtonSetting[]): DynamicFormButtonSetting[] => {
    const previous = formButtonsRef.current ?? [];
    if (!previous.length) return next;

    const prevOrder = new Map(previous.filter((b) => b.id).map((b, i) => [b.id!, i]));
    const used = new Set<OdinKey>();
    const ordered: DynamicFormButtonSetting[] = [];
    const visibleRest: DynamicFormButtonSetting[] = [];
    const hiddenRest: DynamicFormButtonSetting[] = [];

    for (const btn of next) {
      if (!btn.id) {
        visibleRest.push(btn);
        continue;
      }

      if (!btn.hidden && prevOrder.has(btn.id)) {
        ordered.push(btn);
        used.add(btn.id);
        continue;
      }

      if (!btn.hidden) {
        visibleRest.push(btn);
        used.add(btn.id);
        continue;
      }

      hiddenRest.push(btn);
    }

    ordered.sort((a, b) => prevOrder.get(a.id!)! - prevOrder.get(b.id!)!);

    return [...ordered, ...visibleRest, ...hiddenRest];
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Mount-only effect: sets up form subscriptions, all dependencies are stable or refs
  useEffect(() => {
    // Capture ref value at start for cleanup
    const destroySubjectCurrent = destroySubject.current;
    // the purpose of the submit observable is so the end user can save how they wish and then tell us the result
    const submitObservable = new Observable<OdinKey | undefined>((subscriber) => {
      dataSender.current = new OdinDataSender<OdinKey | undefined>(subscriber);
    });
    submitObservable.pipe(takeUntil(destroySubject.current)).subscribe((submitResult) => {
      if (onPostSubmit) {
        if (submitResult !== undefined && afterSubmitObservables.current.length > 0) {
          const observables = afterSubmitObservables.current.map(
            (afterSubmit) =>
              new Observable<void>((subscriber) => afterSubmit(submitResult)(new OdinDataSender<void>(subscriber)))
          );
          forkJoin(observables).subscribe(() => onPostSubmit(submitResult));
        }
      }
    });

    DynamicFieldService.getServiceInstance();

    dataProxyService.current = DataProxyInstanceService.getServiceInstance(dynamicFormId);

    dataProxyService.current
      .onDataChange()
      .pipe(takeUntil(destroySubject.current))
      .subscribe((dataChange) => {
        if (dataChange) {
          const { property, oldValue, newValue } = dataChange;
          dataChangeValidation(property, oldValue, newValue);
        }
      });

    dataProxyService.current
      .onProxyCreated()
      .pipe(debounceTime(500), takeUntil(destroySubject.current))
      .subscribe((createdDataProxy) => {
        if (createdDataProxy) {
          visibilityConditionProcessor.current?.loadData(createdDataProxy, formButtons);

          if (parentFieldMap.current) {
            const dataKeys = Object.keys(createdDataProxy).filter((key) => parentFieldMap.current?.has(key));
            parentValue.value = [];
            for (let i = 0, length = dataKeys.length; i < length; i++) {
              const dataKey = dataKeys[i];
              const dataFieldType = getFieldSettingById(dataKey, fieldsRef.current ?? [])?.fieldType;
              parentValue.value.push({
                parentFieldId: dataKey,
                fieldValue: createdDataProxy[dataKey],
                type: dataFieldType,
                initialValue: true,
              });
            }
          }
        }

        setDataProxy(createdDataProxy);
      });

    visibilityConditionProcessor.current = new VisibilityConditionProcessor(
      settings.visibilityConditions || [],
      settings.workflowStepVisibilityConditions || [],
      data || dataProxyService.current.getRawData(),
      settings.fields,
      buttons,
      settings.userGroups
    );

    visibilityConditionProcessor.current
      ?.onFieldVisibilityChanged()
      .pipe(takeUntil(destroySubject.current))
      .subscribe((newFields) => {
        if (newFields) {
          fieldsRef.current = newFields;
          setFields([...newFields]);
          checkAndFireGroupsExpandedOrCollapsed();
          onFormFieldsChanged?.(newFields);
        }
      });

    visibilityConditionProcessor.current
      ?.onButtonVisibilityChanged()
      .pipe(takeUntil(destroySubject.current))
      .subscribe((newButtons) => {
        if (!newButtons) return;
        setFormButtons(reconcileButtonsByVisibility(newButtons));
      });

    return () => {
      destroySubjectCurrent.next();
      destroySubjectCurrent.complete();

      DataProxyInstanceService.removeServiceInstance(dynamicFormId);
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional: loadData is stable, only run when data prop changes
  useEffect(() => {
    if (data) {
      loadData(data);
      dataProxyService.current?.createDataProxy(data);
    }
  }, [data]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional: checkAndFireGroupsExpandedOrCollapsed and fields are used for comparison, not as triggers
  useEffect(() => {
    if (settings.fields && !isEqual(settings.fields, fields)) {
      parentFieldMap.current = buildParentFieldMap(settings.fields);
      fieldsRef.current = settings.fields;
      setFields(cloneDeep(fieldsRef.current));
      checkAndFireGroupsExpandedOrCollapsed();

      visibilityConditionProcessor.current?.updateFields(settings.fields);
    }
  }, [settings.fields]);

  useEffect(() => {
    if (buttons && buttons.length > 0) {
      if (settings.workflowStepVisibilityConditions) {
        if (
          !workflowStepVisibilityConditionsRef.current ||
          !isEqual(workflowStepVisibilityConditionsRef.current, settings.workflowStepVisibilityConditions) ||
          !isEqual(buttons, formButtons)
        ) {
          workflowStepVisibilityConditionsRef.current = settings.workflowStepVisibilityConditions;
          visibilityConditionProcessor.current?.updateButtons(
            settings.workflowStepVisibilityConditions,
            buttons,
            formButtons
          );
        }
      } else if (!isEqual(buttons, formButtons)) {
        visibilityConditionProcessor.current?.updateButtons([], buttons, formButtons);
      }
    }
  }, [buttons, formButtons, settings.workflowStepVisibilityConditions]);

  const visibleFieldsIds = useMemo(() => {
    return fields
      ?.filter((section) => !isFieldHidden(section))
      .map((section) => ({
        ...section,
        fields: section.fields?.filter((field) => !isFieldHidden(field)) || [],
      }))
      .flatMap((section) => [String(section.id), ...(section.fields?.map((field) => String(field.id)) || [])]);
  }, [fields]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional: expandOnError, scrollFieldIntoView, visibleFieldsIds are stable helpers
  useEffect(() => {
    if (hfErrors && Object.keys(hfErrors).length > 0) {
      expandOnError();
      const allErrorFieldsKeys = Object.keys(hfErrors);
      const firstErrorFieldId = visibleFieldsIds?.find((id) => allErrorFieldsKeys.includes(id));
      setTimeout(() => scrollFieldIntoView(firstErrorFieldId ?? ''), 200); //Timeout before scrolling in case the section is collapsed
    }
  }, [hfErrors]);

  useEffect(() => {
    if (settings.customFieldComponents) {
      DynamicFieldService.getServiceInstance(settings.customFieldComponents);
    }
  }, [settings.customFieldComponents]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional: validationEnabled is checked conditionally, _submitForm is stable
  useEffect(() => {
    // only if validation has been turned off do we want to submit the form
    if (!isValidationEnabled && validationEnabled) {
      _submitForm();
    }
  }, [isValidationEnabled]);

  const checkAndFireGroupsExpandedOrCollapsed = () => {
    const visibleFieldGroups = fieldsRef.current?.filter((field) => !isFieldHidden(field)) ?? [];
    if (visibleFieldGroups.length > 0) {
      if (visibleFieldGroups.every((field) => field.startOpen === true)) {
        onAllGroupsExpanded?.();
      } else if (visibleFieldGroups.every((field) => field.startOpen === false)) {
        onAllGroupsCollapsed?.();
      }
    }
  };

  const onGroupChanged = (groupId: OdinKey, isOpen: boolean) => {
    const foundField = fieldsRef.current?.find((field) => field.id === groupId);
    if (fieldsRef.current && foundField) {
      foundField.startOpen = isOpen;
      setFields([...fieldsRef.current]);
      checkAndFireGroupsExpandedOrCollapsed();
    }
  };

  const modifyStartOpen = (startOpen: boolean) => {
    if (fieldsRef.current) {
      for (let i = 0, length = fieldsRef.current.length; i < length; i++) {
        if (
          fieldsRef.current[i].fieldType === 'GROUP' ||
          (fieldsRef.current[i].fieldType === 'CUSTOM' && fieldsRef.current[i].wrapInGroup === true)
        ) {
          fieldsRef.current[i].startOpen = startOpen;
        }
      }
      setFields([...fieldsRef.current]);
    }
  };

  const _submitForm = () => {
    if (submitButtonRef.current) {
      isAllowedToSubmit.current = true;
      submitButtonRef.current.click();
    }
  };

  const expandOnError = () => {
    if (fields) {
      let changed = false;
      const _formFields = [...fields];
      const sectionsContainingErrors = Object.keys(hfErrors).map(String);
      for (let i = 0, length = fields.length; i < length; i++) {
        if (!fields[i].startOpen) {
          const sectionHasErrors =
            fields[i]?.fields?.some((section) => sectionsContainingErrors.includes(String(section?.id))) ||
            sectionsContainingErrors.includes(fields[i].fieldName);
          if (sectionHasErrors) {
            _formFields[i].startOpen = true;
            changed = true;
          }
        }
      }
      if (changed) {
        setFields(_formFields);
      }
    }
  };

  // imperative handle methods
  const expandAllGroups = () => {
    modifyStartOpen(true);
  };

  const collapseAllGroups = () => {
    modifyStartOpen(false);
  };

  const submitForm = (disableValidation = false) => {
    // if we don't need to disable the validation or the validation is already disabled then submit the form
    if (!disableValidation || disableValidation !== validationEnabled) {
      _submitForm();
    } else {
      // we need to turn off validation before submitting the form
      setIsValidationEnabled(false);
    }
  };

  const moveGroupToTop = (groupId: string) => {
    document.getElementById(_fieldGroupId(dynamicFormId, groupId))?.scrollIntoView();
  };

  const scrollFieldIntoView = (fieldId: string) => {
    document.getElementById(_fieldId(dynamicFormId, fieldId))?.scrollIntoView();
  };

  const loadData = (data: JsonDataItem) => {
    const dataKeys = Object.keys(data);
    for (const key of dataKeys) {
      setValue(key, data[key]);
    }
  };

  const getData = () => {
    return getValues();
  };

  const setFieldError = (name: string, error: ErrorOption) => {
    return setError(name, error);
  };

  /**
   * According to the GitHub issue mentioned below the isDirty calculation is not always accurate so one of the
   * suggestions was to check if the dirtyFields object is empty or not.
   * https://github.com/react-hook-form/react-hook-form/issues/4740
   * @returns {boolean} true if the form is dirty.
   */
  const formIsDirty = () => !isEmptyObject(dirtyFields);

  const formDirtyFields = () => dirtyFields;

  const resetFormState = (currentState: JsonDataItem) => {
    for (let i = 0; fields && i < fields.length; i++) {
      const field = fields[i];
      if (field.fields) {
        for (let j = 0; j < field.fields.length; j++) {
          const groupFields: DynamicFormField = field.fields[j];
          const groupFieldIdKey = String(groupFields.id);
          // biome-ignore lint/suspicious/noPrototypeBuiltins: can't change this to hasOwn - using out of date target library in tsconfig
          if (currentState.fields && !Object.prototype.hasOwnProperty.call(currentState.fields, groupFieldIdKey)) {
            // undefined is not a valid defaultValue (https://legacy.react-hook-form.com/api/usecontroller/controller)
            currentState[groupFieldIdKey] = null;
          }
        }
      }
    }
    reset(currentState);
  };

  const resetFormField = (name: string, value?: unknown) => {
    resetField(name, { defaultValue: value });
  };

  const setFieldValue = (name: string, value: unknown) => {
    const dataProxy = dataProxyService?.current?.dataProxy;

    if (dataProxy) {
      dataProxy[name] = value;
      setValue(name, value, { shouldDirty: true });
    }
  };

  useImperativeHandle(ref, () => ({
    expandAllGroups,
    collapseAllGroups,
    submitForm,
    moveGroupToTop,
    scrollFieldIntoView,
    loadData,
    getData,
    formIsDirty,
    formDirtyFields,
    resetFormState,
    resetFormField,
    setFieldValue,
    setFieldError,
    validate: trigger,
    getFieldErrors: () => hfErrors,
  }));

  const setSubmitButtonRef = (button: HTMLButtonElement) => (submitButtonRef.current = button);

  const onFormSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    // prevent event from bubbling up, in case form is embedded in another form
    event.preventDefault();
    event.stopPropagation();

    if (isAllowedToSubmit.current) {
      // Set a timeout to allow components onBlur to trigger before the form submits if the user submits without clicking outside the field
      setTimeout(() => handleSubmit(onHookFormSubmit, onError)(event));
    }

    isAllowedToSubmit.current = false;
  };

  const onHookFormSubmit = async () => {
    const currentFormData = dataProxyService.current?.getRawData() || {};
    let finalData = currentFormData;

    if (onPreSubmit) {
      onGetPreSubmitData?.();
      const preSubmitData = await getPreSubmitData(beforeSubmitObservables.current);
      finalData = onPreSubmit(preSubmitData, currentFormData);
    }

    // dataSender should never be undefined by this point but TypeScript doesn't know that
    if (dataSender.current) {
      onSubmit(finalData, dataSender.current);
    }

    // Suppress dirty-on-show for visibility changes triggered by post-submit field additions
    isPostSubmitRef.current = true;

    // only if isValidationEnabled is different from the validationEnabled property do we want to fire the state change
    if (isValidationEnabled !== validationEnabled) {
      setIsValidationEnabled(validationEnabled);
    }
  };

  const parentProperties = useCallback(
    (renderField: DynamicFormField) => {
      if (renderField.parentFieldId && fields) {
        const parentField = getFieldSettingById(renderField.parentFieldId, fields);
        if (parentField) {
          return {
            parentLabel: parentField.caption,
          };
        }
      }

      return {};
    },
    [fields]
  );

  const renderDynamicField = (field: DynamicFormField, isHidden: boolean, dynamicProperties?: JsonDataItem) => {
    const resetField = (value?: unknown) => resetFormField(field.fieldName, value);
    const setFieldValue = (value?: unknown) =>
      setValue(field.fieldName, value, { shouldDirty: !isPostSubmitRef.current });
    const customFieldType = field.customFieldType;

    const fieldValueConverter =
      settings.fieldValueConverters?.[field.fieldType] ??
      (customFieldType ? settings.fieldValueConverters?.[customFieldType] : undefined);

    // LABEL fields always use field.fieldData (form schema) because the record
    // API returns lossy API URLs that the WebView cannot resolve from file://.
    const fieldData = field.fieldType === 'LABEL' ? field.fieldData : dataProxy?.[field.fieldName];

    // The `?? null` fallback ensures the value is never `undefined`, which
    // react-hook-form treats as "no value provided", causing resetField to
    // silently restore a previously registered default instead of clearing it.
    const fieldValue = fieldData ?? field.fieldData ?? null;

    return (
      <DynamicField
        key={_fieldId(dynamicFormId, field.id)}
        scrollId={_fieldId(dynamicFormId, field.id)}
        id={field.id}
        dynamicFormId={dynamicFormId}
        value={fieldValue}
        fieldName={field.fieldName}
        fieldType={field.fieldType}
        caption={field.caption}
        required={field.required}
        error={field.fieldName && errors?.[field.fieldName]?.message}
        dynamicProperties={dynamicProperties}
        readOnly={readOnly || field.permissions?.edit === false}
        customFieldType={customFieldType}
        helpMessage={field.helpMessage}
        hintMessage={field.hintMessage}
        fieldValueConverter={fieldValueConverter}
        permissions={field.permissions}
        hidden={isHidden}
        labelStyles={field.labelStyles}
        resetField={resetField}
        setFieldValue={setFieldValue}
      />
    );
  };

  const submitButton = useMemo(() => {
    return (
      <Button
        ref={(button) => {
          if (button) setSubmitButtonRef(button);
        }}
        htmlType="submit"
        hidden={true}
      >
        Submit
      </Button>
    );
  }, []);

  const groupStyles = 'bg-mono-1 rounded-md border border-gray-4 p-2 [&:has(a:focus)]:border-primary-2';

  return (
    <form
      key={dynamicFormId}
      id={dynamicFormId}
      onSubmit={onFormSubmit}
      spellCheck={false}
      className="flex w-full flex-col overflow-hidden"
    >
      <div className="custom-scroll flex-grow overflow-y-auto">
        {dataProxy &&
          fields?.map((field) => {
            const groupHeaderStyles = clsx('focus:border-gray-2! focus:border-b!', {
              'border-transparent focus:border-transparent!': (field.startOpen || false) === false,
            });

            if (field.fieldType === 'GROUP') {
              const isGroupHidden = isFieldHidden(field);

              return (
                <Accordion
                  key={_fieldGroupId(dynamicFormId, field.id)}
                  id={_fieldGroupId(dynamicFormId, field.id)}
                  isOpen={field.startOpen || false}
                  title={field.caption}
                  hidden={isGroupHidden}
                  onClick={(isOpen) => onGroupChanged(field.id, isOpen)}
                  wrapperStyles={groupStyles}
                  headerStyles={groupHeaderStyles}
                  childWrapperStyles={field.childWrapperStyles}
                >
                  {field.fields?.map((groupField) => {
                    const properties = {
                      control,
                      register,
                      trigger,
                      ...settings.globalProperties,
                      ...groupField.dynamicProperties,
                      ...groupField.dataSettings,
                      ...registerMethods,
                      ...parentProperties(groupField),
                      parentFieldId: groupField.parentFieldId,
                      parentValue,
                    };

                    if (dataRetrieval && isDataLoadedField(groupField)) {
                      properties.data = dataRetrieval;
                    }

                    const isGroupFieldHidden = isFieldHidden(groupField);
                    groupField = {
                      ...groupField,
                      permissions: {
                        read: field.permissions?.read,
                        edit: field.permissions?.edit,
                      },
                    };

                    return renderDynamicField(groupField, isGroupFieldHidden, properties);
                  })}
                </Accordion>
              );
            } else {
              const fieldProperties = {
                control,
                register,
                trigger,
                ...settings.globalProperties,
                ...field.dynamicProperties,
                ...field.dataSettings,
                ...registerMethods,
                ...parentProperties(field),
                parentFieldId: field.parentFieldId,
                parentValue,
              };

              if (dataRetrieval && isDataLoadedField(field)) {
                fieldProperties.data = dataRetrieval;
              }

              const isHidden = isFieldHidden(field);

              return field.fieldType === 'CUSTOM' && field.wrapInGroup === true ? (
                <Accordion
                  key={_fieldGroupId(dynamicFormId, field.id)}
                  id={_fieldGroupId(dynamicFormId, field.id)}
                  isOpen={field.startOpen || false}
                  title={field.customGroupTitle || ''}
                  hidden={isHidden}
                  onClick={(isOpen) => onGroupChanged(field.id, isOpen)}
                  wrapperStyles={groupStyles}
                  headerStyles={groupHeaderStyles}
                  childWrapperStyles={field.childWrapperStyles}
                >
                  {renderDynamicField(field, isHidden, fieldProperties)}
                </Accordion>
              ) : (
                renderDynamicField(field, isHidden, fieldProperties)
              );
            }
          })}
        {!showSubmitButtons && submitButton}
      </div>
      {showSubmitButtons && formButtons && (
        <div
          ref={tagCreator('all-button')}
          className={clsx(
            'custom-scroll flex max-h-28 shrink-0 gap-x-2 gap-y-4 overflow-y-auto p-1',
            {
              'justify-end': buttonsLocation === DynamicFormButtonLocation.Right,
              'items-stretch border-t border-solid border-gray-4 py-2':
                buttonsPosition === DynamicFormButtonPosition.Fixed,
            },
            buttonStyles
          )}
        >
          {formButtons.map((button, index) => {
            const fullText = button.text;
            const showTooltip = fullText.length > 24;
            const displayText = showTooltip ? fullText.substring(0, 21) + '...' : fullText;
            const disabled = button.disabled === undefined ? readOnly : button.disabled;
            return (
              <Button
                key={`button_${button.name}_${index}`}
                fullWidth={false}
                htmlType={button.htmlType}
                type={button.type}
                variant={button.variant}
                classNames={button.className}
                disabled={disabled}
                hidden={button.hidden}
                onClick={() => {
                  if (button.htmlType === 'submit') {
                    isAllowedToSubmit.current = true;
                  }

                  button.onClick?.();
                }}
              >
                <TooltipLegacy
                  description={fullText}
                  tooltipClassName="bg-mono-1"
                  disabled={!showTooltip}
                  tooltipZIndexCheck={true}
                  key={`button_tooltip_${button.name}_${index}`}
                  tooltipTargetPosition={DomTargetPosition.TopRight}
                  tooltipAlignment={DomElementAlignment.TopRight}
                >
                  {displayText}
                </TooltipLegacy>
              </Button>
            );
          })}
          {submitButton}
        </div>
      )}
    </form>
  );
};

export default forwardRef(DynamicForm);
